import { formatCodeableConcept, type WithId } from '@medplum/core';
import type { DocumentReference, Encounter } from '@medplum/fhirtypes';
import type { TFunction } from 'i18next';
import { ArrowLeftIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../utils/date';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { EncounterDocumentsSection } from './EncounterDocumentsSection';

export type EncounterDetailProps = {
  encounter: WithId<Encounter>;
  onBack?: () => void;
  documents?: DocumentReference[];
  isLoadingDocuments?: boolean;
  /**
   * Resolved display names for `Encounter.participant[*].individual.reference` values
   * the consumer fetched out-of-band. The detail view prefers (1) the reference's own
   * `display`, (2) this map's entry, (3) the raw reference string.
   */
  participantDisplays?: Record<string, string>;
};

type DetailSection = { title: string; content: React.ReactNode };

const resolveProviders = (encounter: Encounter, participantDisplays: Record<string, string> | undefined): string[] =>
  encounter.participant
    ?.map((p) => {
      const ref = p.individual?.reference;
      return p.individual?.display ?? (ref ? participantDisplays?.[ref] : undefined) ?? ref;
    })
    .filter((v): v is string => !!v) ?? [];

const getSections = (
  encounter: Encounter,
  participantDisplays: Record<string, string> | undefined,
  t: TFunction<'records'>,
): DetailSection[] => {
  const sections: DetailSection[] = [];

  const visitClass = encounter.class?.display ?? encounter.class?.code;
  if (visitClass) sections.push({ title: t('visitClass'), content: <div>{visitClass}</div> });

  const providers = resolveProviders(encounter, participantDisplays);
  if (providers.length > 0) {
    sections.push({
      title: t('provider'),
      content: (
        <ul className="list-inside list-disc">
          {providers.map((provider) => (
            <li key={provider}>{provider}</li>
          ))}
        </ul>
      ),
    });
  }

  const periodStart = encounter.period?.start;
  const periodEnd = encounter.period?.end;
  if (periodStart || periodEnd) {
    sections.push({
      title: t('dateAndTime'),
      content: (
        <>
          {periodStart && (
            <div>
              <span className="text-muted-foreground">{t('start')} </span>
              {formatDateTime(periodStart)}
            </div>
          )}
          {periodEnd && (
            <div>
              <span className="text-muted-foreground">{t('end')} </span>
              {formatDateTime(periodEnd)}
            </div>
          )}
        </>
      ),
    });
  }

  const location = encounter.location?.at(0)?.location?.display;
  if (location) sections.push({ title: t('location'), content: <div>{location}</div> });

  const reasonCodes = encounter.reasonCode?.map((r) => formatCodeableConcept(r)).filter(Boolean) ?? [];
  if (reasonCodes.length > 0) {
    sections.push({
      title: t('reasonForVisit'),
      content: (
        <ul className="list-inside list-disc">
          {reasonCodes.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      ),
    });
  }

  return sections;
};

export const EncounterDetail = ({
  encounter,
  onBack,
  documents,
  isLoadingDocuments,
  participantDisplays,
}: EncounterDetailProps): React.ReactElement => {
  const { t } = useTranslation('records');
  const type = formatCodeableConcept(encounter.type?.at(0)) || t('unknownEncounter');
  const status = encounter.status || t('unknown');
  const sections = getSections(encounter, participantDisplays, t);

  return (
    <div className="mx-auto w-full max-w-4xl p-5" data-testid="encounter-detail-view">
      {onBack && (
        <Button
          id="back-to-encounters-button"
          variant="ghost"
          onClick={onBack}
          className="mb-3 flex items-center gap-2"
        >
          <ArrowLeftIcon className="size-4" />
          <span>{t('backToEncounters')}</span>
        </Button>
      )}

      <div className="mb-3 flex items-center gap-2">
        <h1 className="text-sm font-medium">{type}</h1>
        <Badge variant="outline">{status}</Badge>
      </div>

      <div className="space-y-3 text-sm">
        {sections.map(({ title, content }) => (
          <div key={title}>
            <h2 className="mb-1 text-xs font-semibold text-muted-foreground">{title}</h2>
            {content}
          </div>
        ))}

        <EncounterDocumentsSection documents={documents} isLoading={isLoadingDocuments} />
      </div>
    </div>
  );
};
